import { content, languages, type Language } from './siteContent'
import { routeFor } from './routes'

export const publicRouteSlugs = ['', 'services', 'portfolio', 'about', 'contact', 'privacy'] as const
type PublicRouteSlug = (typeof publicRouteSlugs)[number]

type SeoEntry = { title: string; description: string; socialImage: string; socialImageAlt: string }

const localizedSeo: Record<Language, Record<PublicRouteSlug, SeoEntry>> = {
  el: {
    '': {
      title: 'Web Developer Αθήνα, Digital Products & AI Media',
      description: 'Ο Φώτης Μύτικας δημιουργεί ιστοσελίδες, web εφαρμογές και ψηφιακά προϊόντα και αναλαμβάνει AI video, virtual personas, mixing και mastering στην Αθήνα και απομακρυσμένα.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Fotis Mytikas Digital Services — web development, digital products, AI video και audio',
    },
    services: {
      title: 'Ιστοσελίδες, Web Εφαρμογές, AI Video & Ήχος',
      description: 'Κατασκευή ιστοσελίδων, landing pages, web εφαρμογών και ψηφιακών προϊόντων, AI video και persona production, video editing, mixing και mastering.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Υπηρεσίες web development, AI video και παραγωγής ήχου από τον Φώτη Μύτικα',
    },
    portfolio: {
      title: 'Portfolio Web, AI Persona & Ψηφιακά Προϊόντα',
      description: 'Δείτε έργα του Φώτη Μύτικα για NeoFlex Hellas, Roselune Everly και WedBeas σε web development, AI persona, multimedia και apps.',
      socialImage: '/neoflexhellasgrprojectsite1.webp',
      socialImageAlt: 'Επιλεγμένα έργα ανάπτυξης ιστοσελίδων και ψηφιακών προϊόντων του Φώτη Μύτικα',
    },
    about: {
      title: 'Web Developer & Digital Product Builder στην Ελλάδα',
      description: 'Γνωρίστε τον Φώτη Μύτικα, Web Developer και δημιουργό ψηφιακών προϊόντων στην Αθήνα, AI Video & Audio Producer και δημιουργό του WedBeas.',
      socialImage: '/fotis-mytikas-profile.webp',
      socialImageAlt: 'Επαγγελματικό πορτρέτο του Φώτη Μύτικα',
    },
    contact: {
      title: 'Επικοινωνία & Προσφορά για Digital Project',
      description: 'Ζητήστε προσφορά για ιστοσελίδα, landing page, web εφαρμογή, ψηφιακό προϊόν, AI video, AI persona, επεξεργασία video, mixing ή mastering.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Επικοινωνία με το Fotis Mytikas Digital Services',
    },
    privacy: {
      title: 'Πολιτική Απορρήτου & Cookies',
      description: 'Πώς το Fotis Mytikas Digital Services χειρίζεται δεδομένα επικοινωνίας, Netlify Forms, δικαιώματα απορρήτου, cookies και τοπική αποθήκευση.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Fotis Mytikas Digital Services',
    },
  },
  en: {
    '': {
      title: 'Web Developer Greece, Digital Products & AI Media',
      description: 'Fotis Mytikas builds websites, web applications and digital products and produces AI video, virtual personas, multimedia, mixing and mastering in Greece and remotely.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Fotis Mytikas Digital Services — web development, digital products, AI video and audio',
    },
    services: {
      title: 'Websites, Web Apps, AI Video & Audio Services',
      description: 'Website, landing page, web application and digital product development, AI video and persona production, video editing, mixing and mastering from Greece worldwide.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Web development, AI video and audio production services by Fotis Mytikas',
    },
    portfolio: {
      title: 'Web, AI Persona & Digital Product Portfolio',
      description: 'Explore Fotis Mytikas projects for NeoFlex Hellas, Roselune Everly and WedBeas across web development, AI personas, multimedia and apps.',
      socialImage: '/neoflexhellasgrprojectsite1.webp',
      socialImageAlt: 'Selected web development and digital product work by Fotis Mytikas',
    },
    about: {
      title: 'Web Developer & Digital Product Builder Greece',
      description: 'Meet Fotis Mytikas, a Web Developer and digital product builder in Greece, AI Video & Audio Producer and creator of WedBeas.',
      socialImage: '/fotis-mytikas-profile.webp',
      socialImageAlt: 'Professional portrait of Fotis Mytikas',
    },
    contact: {
      title: 'Contact & Digital Project Quote',
      description: 'Request a quote for a website, landing page, web application, digital product, AI video, AI persona, video editing, audio mixing or mastering.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Contact Fotis Mytikas Digital Services',
    },
    privacy: {
      title: 'Privacy & Cookie Policy',
      description: 'How Fotis Mytikas Digital Services handles contact data, Netlify Forms, privacy rights, cookies and local browser storage.',
      socialImage: '/fotis-mytikas-hero.webp',
      socialImageAlt: 'Fotis Mytikas Digital Services',
    },
  },
}

export const prerenderRoutes = languages.flatMap((language) => publicRouteSlugs.map((slug) => routeFor(language, slug)))

export type RouteSeo = {
  language: Language
  slug: string
  path: string
  alternatePaths: Record<Language, string>
  pageTitle: string
  title: string
  description: string
  indexable: boolean
  ogLocale: 'en_US' | 'el_GR'
  socialImage: string
  socialImageAlt: string
  faq?: readonly { question: string; answer: string }[]
}

export function getRouteSeo(pathname: string): RouteSeo {
  const cleanPath = pathname.split(/[?#]/, 1)[0]
  const segments = cleanPath.split('/').filter(Boolean)
  const language: Language = segments[0] === 'en' ? 'en' : 'el'
  const slug = (language === 'en' ? segments.slice(1) : segments).join('/').replace(/\/+$/, '')
  const copy = content[language]

  let pageTitle: string
  let description: string
  let socialImage = '/fotis-mytikas-hero.webp'
  let socialImageAlt = 'Fotis Mytikas Digital Services'
  let indexable = true

  if (publicRouteSlugs.includes(slug as PublicRouteSlug)) {
    const entry = localizedSeo[language][slug as PublicRouteSlug]
    pageTitle = entry.title
    description = entry.description
    socialImage = entry.socialImage
    socialImageAlt = entry.socialImageAlt
  } else if (slug === 'thank-you') {
      pageTitle = copy.thankYou.eyebrow
      description = copy.thankYou.text
      indexable = false
  } else {
      pageTitle = copy.notFound.eyebrow
      description = copy.notFound.text
      indexable = false
  }

  return {
    language,
    slug,
    path: routeFor(language, slug),
    alternatePaths: { en: routeFor('en', slug), el: routeFor('el', slug) },
    pageTitle,
    title: `${pageTitle} | Fotis Mytikas Digital Services`,
    description,
    indexable,
    ogLocale: language === 'el' ? 'el_GR' : 'en_US',
    socialImage,
    socialImageAlt,
    ...(slug === 'services' ? { faq: copy.services.faq } : {}),
  }
}
